<template>
  <div
    :class="{
      'main-search': type === 'main',
      'default-search': type === 'default',
      'search-large': size === 'large' && type === 'default',
    }"
  >
    <a-input-search
      v-if="type === 'main'"
      ref="inputRef"
      v-model:value="keyword"
      :maxlength="isChinese() ? 32 : 250"
      size="large"
      allowClear
      :placeholder="placeholder"
      class="search-input"
      @search="onSearch"
      @change="onChange"
      @input="val => $emit('input', val)"
      @compositionstart="$emit('compositionstart')"
      @compositionend="$emit('compositionend')"
      @focus="onFocus"
    >
      <template #prefix>
        <iconpark-icon
          class="icon-size text-[#121926] mr-[5px]"
          name="search"
          size="20"
        ></iconpark-icon>
      </template>
      <template #suffix>
        <a-button type="primary" class="rounded-[20px] btns" @click="onSearch(keyword)">
          <span>{{ $t('sou-suo') }}</span>
        </a-button>
      </template>
    </a-input-search>
    <a-input-search
      v-else
      ref="inputRef"
      v-model:value="keyword"
      :placeholder="placeholder"
      allowClear
      class="w-[100%] text-[18px]"
      size="large"
      :maxlength="isChinese() ? 32 : 250"
      @search="onSearch"
      @change="onChange"
      @input="val => $emit('input', val)"
      @compositionstart="$emit('compositionstart')"
      @compositionend="$emit('compositionend')"
      @focus="onFocus"
    >
      <template #prefix>
        <iconpark-icon class="icon-size text-font-1 mr-[5px]" name="search"></iconpark-icon>
      </template>
    </a-input-search>
  </div>
</template>

<script lang="ts" setup>
import { isChinese } from '~/utils/area';

const props = withDefaults(
  defineProps<{
    type: 'main' | 'default';
    size?: string;
    modelValue?: string;
    placeholder?: string;
    max?: number;
    showResult?: boolean;
    showHot?: boolean;
    position?: string;
  }>(),
  {
    type: 'main',
  },
);

const inputRef = ref();

const emits = defineEmits([
  'search',
  'change',
  'focus',
  'update:modelValue',
  'input',
  'compositionstart',
  'compositionend',
]);

// const searched = ref(false)

const keyword = computed({
  get: () => props.modelValue,
  set: v => {
    emits('update:modelValue', v?.trim());
  },
});

const onSearch = (val: string, event: any) => {
  if (event?.type === 'click') {
    return;
  }

  emits('search', val);
};

const onFocus = () => {
  emits('focus');
};

const onChange = (val: string) => {
  emits('change', val);
};

defineExpose({
  inputRef,
});
</script>

<style lang="scss" scoped>
.main-search {
  :deep(.ant-input-affix-wrapper) {
    border-radius: 28px !important;
    padding: 6px 6px 6px 18px;
    height: 48px;
    border-color: var(--back-5);
    .ant-input-clear-icon {
      color: rgba(18, 25, 38, 0.25) !important;
      font-size: 16px;
    }
    &:hover {
      border-color: var(--purple-6) !important;
    }
  }
  :deep(.ant-input-wrapper) {
    .ant-btn {
      @apply h-[36px] min-w-[68px];
      font-weight: bold;
      border-radius: 20px !important;
      background: rgba(18, 25, 38, 1);
      color: white;
      border: none;
      letter-spacing: 0;
      box-shadow: none;
    }
    &:hover {
      .ant-btn {
        border-radius: 20px !important;
        color: white;
        background: #6962ff;
      }
    }
  }

  :deep(.ant-input-affix-wrapper-focused) {
    border-color: #6962ff !important;
    opacity: 1;
  }
  :deep(.ant-input) {
    font-size: 14px;
  }
  :deep(.ant-input-group-addon) {
    display: none;
  }
}

.default-search {
  @apply relative rounded-[16px];
  :deep(.ant-input-group-addon) {
    display: none;
  }

  :deep(.ant-input) {
    &::placeholder {
      @apply text-font-40;
    }
  }

  .icon-size {
    @apply text-[18px];
  }

  &-result-content {
    @apply absolute left-0 w-full;
    z-index: 10000;
    // display: none;
    .main {
      @apply p-[20px] mt-[5px] bg-back-1;
      box-shadow: 0px 6px 20px 0px rgba(81, 80, 96, 0.2);
      border-radius: 20px;
    }
  }
}

.search-large {
  box-shadow: 0px 3px 20px 0px rgba(21, 46, 106, 0.1);

  .icon-size {
    @apply text-[28px];
  }
  :deep(.ant-input-lg) {
    &::placeholder {
      @apply text-font-50;
    }
  }

  :deep(.ant-input-affix-wrapper-focused) {
    border-color: var(--purple-6) !important;
    opacity: 1;
  }
  :deep(.ant-input-affix-wrapper) {
    padding: 15px 24px;
    font-size: 18px;
    border-color: transparent;
    border-radius: 16px !important;
  }
  :deep(.ant-input-clear-icon) {
    @apply text-[18px];
  }
}

.search-input {
  :deep(.ant-input-affix-wrapper-lg) {
    background-color: #fff;
  }

  :deep(.ant-input) {
    background-color: #fff;
    color: #121926 !important;

    &::placeholder {
      color: var(--text-1-30);
    }
  }

  :deep(.ant-input::placeholder) {
    color: rgba(18, 25, 38, 0.3);
  }
}
</style>
